// frontend/src/pages/devioz/DeviozDashboard.tsx
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Chart from 'chart.js/auto';
import { getSalesDataset, type NormalizedSale } from '../../services/tenantData';
import type { TenantSlug } from '../../config/tenants';
import analyticsPNG from '../../assets/illustrations/dashboard-analytics.png';

/* =========================================================
   Utilidades y tipos
   ========================================================= */

type KPI = { totalPedidos: number; totalMonto: number; promedioPedido: number };
type Conteo = Record<string, number>;
type MontoPorClave = Record<string, number>;

/** Conjunto oficial del dashboard (incluye gloria) */
const TENANTS_GLOBALES: TenantSlug[] = ['bcp', 'bembos', 'claro', 'plazavea', 'gloria'];

/** Paleta / metadata para UI */
const EMPRESAS_UI = [
  { slug: 'bcp' as TenantSlug, label: 'BCP', color: '#0ea5e9' },
  { slug: 'bembos' as TenantSlug, label: 'Bembos', color: '#f97316' },
  { slug: 'claro' as TenantSlug, label: 'Claro', color: '#22c55e' },
  { slug: 'plazavea' as TenantSlug, label: 'PlazaVea', color: '#a855f7' },
  { slug: 'gloria' as TenantSlug, label: 'Gloria', color: '#dc2626' },
];

/** Nombre legible */
const nombreEmpresa = (slug: TenantSlug | string): string =>
  slug === 'bcp'
    ? 'BCP'
    : slug === 'bembos'
    ? 'Bembos'
    : slug === 'claro'
    ? 'Claro'
    : slug === 'plazavea'
    ? 'PlazaVea'
    : slug === 'gloria'
    ? 'Gloria'
    : String(slug).toUpperCase();

/** PEN */
const PEN = (n: number) =>
  n.toLocaleString('es-PE', { style: 'currency', currency: 'PEN', minimumFractionDigits: 2 });

/** Extensión de la fila con el tenant de origen */
type SaleExt = NormalizedSale & { tenant: TenantSlug };

/** Normalizador numérico */
function toNumber(v: unknown): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v === 'string') {
    const cleaned = v.replace(/[^\d.,-]/g, '').replace(/\./g, '').replace(',', '.');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/** Heurística de monto desde el CSV */
function getMontoReal(f: NormalizedSale): number {
  if (f.monto && Number.isFinite(f.monto) && f.monto !== 0) return f.monto;
  const r = f.raw as Record<string, unknown> | undefined;
  if (!r) return 0;

  const directAliases = [
    'monto', 'Monto',
    'total', 'Total',
    'importe', 'Importe',
    'subtotal', 'Subtotal',
    'valor_venta', 'ValorVenta', 'VALOR_VENTA',
    'monto_total', 'MontoTotal',
    'precio_total', 'PrecioTotal',
    'venta', 'Venta', 'TOTAL',
  ];
  for (const k of directAliases) {
    if (k in r) {
      const val = toNumber(r[k]);
      if (val !== 0) return val;
    }
  }

  // precio * cantidad
  const qtyAliases = ['cantidad', 'Cantidad', 'qty', 'Qty', 'CANTIDAD'];
  const priceAliases = ['precio', 'Precio', 'precio_unitario', 'PrecioUnitario', 'unit_price', 'UnitPrice', 'PRECIO_UNITARIO'];
  let qty = 0;
  let price = 0;
  for (const k of qtyAliases) if (k in r) qty = toNumber(r[k]);
  for (const k of priceAliases) if (k in r) price = toNumber(r[k]);
  const calc = qty * price;
  if (Number.isFinite(calc) && calc !== 0) return calc;

  return 0;
}

/* =========================================================
   DEMO (si los CSV no traen montos)
   ========================================================= */
const DEMO_VENTAS: MontoPorClave = {
  BCP: 250_000,
  Bembos: 180_000,
  Claro: 320_000,
  PlazaVea: 210_000,
  Gloria: 160_000,
};

const DEMO_PEDIDOS: Conteo = {
  BCP: 51,
  Bembos: 102,
  Claro: 102,
  PlazaVea: 51,
  Gloria: 58, // lo que quieres que aparezca en tarjeta
};

/** SKUs demo por empresa */
const DEMO_SKUS: Partial<Record<TenantSlug, string[]>> = {
  bcp: ['Cuenta Digital', 'Tarjeta Crédito Visa', 'Préstamo Express'],
  bembos: ['Combo Clásico', 'Helado Bembos', 'Hamburguesa BBQ'],
  claro: ['Plan Ilimitado', 'Fibra Hogar', 'Paquete Streaming'],
  plazavea: ['Arroz 5kg', 'Aceite 1L', 'Atún 170g'],
  gloria: ['Leche Evaporada', 'Yogurt Fresa', 'Mantequilla'],
};

/* =========================================================
   Hash y generadores determinísticos
   ========================================================= */
function hash01(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  return (h >>> 0) / 4294967296;
}

function generaSerieEmpresa(total: number, labels: string[], seedKey: string): number[] {
  const N = labels.length;
  const pesos: number[] = [];
  for (let i = 0; i < N; i++) {
    const t = i / (N - 1 || 1);
    const base = 0.8 + 0.4 * Math.sin(Math.PI * t);
    const ruido = 0.9 + 0.2 * hash01(seedKey + '|' + labels[i]);
    pesos.push(base * ruido);
  }
  const suma = pesos.reduce((a, b) => a + b, 0) || 1;
  return pesos.map((p) => (p / suma) * total);
}

const pctFor = (key: string) => Math.round(55 + hash01(key) * 40);

/* =========================================================
   Filtro desplegable
   ========================================================= */

type DropdownProps = {
  value: TenantSlug[];
  onChange: (val: TenantSlug[]) => void;
  anchorClassName?: string;
  anchorLabel?: string;
};

function EmpresasDropdown({ value, onChange, anchorClassName, anchorLabel }: DropdownProps) {
  const [open, setOpen] = useState(false);
  const all = value.length === TENANTS_GLOBALES.length;

  const toggleOne = (slug: TenantSlug) => {
    onChange(value.includes(slug) ? value.filter((s) => s !== slug) : [...value, slug]);
  };

  const setAll = () => onChange([...TENANTS_GLOBALES]);

  return (
    <div className="position-relative">
      <button
        type="button"
        className={`btn btn-sm btn-light border ${anchorClassName || ''}`}
        onClick={() => setOpen((v) => !v)}
      >
        {anchorLabel || 'Empresas:'}{' '}
        <b>{all ? 'Todas' : value.map((s) => nombreEmpresa(s)).join(', ') || '—'}</b>
        <span className="ms-1 small">▾</span>
      </button>
      {open && (
        <div
          className="card shadow-sm position-absolute p-3"
          style={{ minWidth: 220, zIndex: 20, right: 0 }}
          onMouseLeave={() => setOpen(false)}
        >
          <div className="fw-bold mb-2">Filtrar empresas</div>
          <div className="form-check mb-2">
            <input className="form-check-input" type="checkbox" checked={all} onChange={setAll} id="dd-all" />
            <label className="form-check-label" htmlFor="dd-all">
              Todas
            </label>
          </div>
          {EMPRESAS_UI.map((e) => (
            <div className="form-check mb-1" key={e.slug}>
              <input
                className="form-check-input"
                type="checkbox"
                id={`dd-${e.slug}`}
                checked={all ? true : value.includes(e.slug)}
                onChange={() => toggleOne(e.slug)}
              />
              <label className="form-check-label" htmlFor={`dd-${e.slug}`} style={{ color: e.color }}>
                ● {e.label}
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* =========================================================
   Componente principal
   ========================================================= */

export default function DeviozDashboard() {
  const [cargando, setCargando] = useState(true);
  const [filas, setFilas] = useState<SaleExt[]>([]);
  const [error, setError] = useState<string | null>(null);

  // filtros
  const [selGraph, setSelGraph] = useState<TenantSlug[]>([...TENANTS_GLOBALES]);
  const [now, setNow] = useState<Date>(new Date());
  const [selCanal, setSelCanal] = useState<TenantSlug[]>([...TENANTS_GLOBALES]);
  const [selTopSku, setSelTopSku] = useState<TenantSlug[]>([...TENANTS_GLOBALES]);
  const [selPromo, setSelPromo] = useState<TenantSlug[]>([...TENANTS_GLOBALES]);

  useEffect(() => {
    document.title = 'Panel • Devioz';
  }, []);
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Cargar datasets
  useEffect(() => {
    let vivo = true;
    (async () => {
      setCargando(true);
      setError(null);
      try {
        const porEmpresa = await Promise.all(
          TENANTS_GLOBALES.map(async (t) => {
            const data = await getSalesDataset(t);
            return data.map((r) => ({ ...r, tenant: t as TenantSlug }));
          })
        );
        if (!vivo) return;
        setFilas(porEmpresa.flat());
      } catch (e: any) {
        if (!vivo) return;
        setError(e?.message || 'No se pudieron cargar los datos.');
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  // Seguridad: no dejar vacíos
  useEffect(() => {
    if (selGraph.length === 0) setSelGraph([...TENANTS_GLOBALES]);
    if (selCanal.length === 0) setSelCanal([...TENANTS_GLOBALES]);
    if (selTopSku.length === 0) setSelTopSku([...TENANTS_GLOBALES]);
    if (selPromo.length === 0) setSelPromo([...TENANTS_GLOBALES]);
  }, [selGraph, selCanal, selTopSku, selPromo]);

  /* ================== KPI ================== */

  // KPIs brutos desde CSV
  const kpiBruto = useMemo<KPI>(() => {
    const totalPedidos = filas.length;
    const totalMonto = filas.reduce((acc, r) => acc + getMontoReal(r), 0);
    const promedioPedido = totalPedidos ? totalMonto / totalPedidos : 0;
    return { totalPedidos, totalMonto, promedioPedido };
  }, [filas]);

  // Conteo real por empresa DESDE CSV
  const pedidosPorEmpresaBruto = useMemo<Conteo>(() => {
    const c: Conteo = {};
    for (const t of TENANTS_GLOBALES) c[nombreEmpresa(t)] = 0;
    for (const r of filas) c[nombreEmpresa(r.tenant)] += 1;
    return c;
  }, [filas]);

  // Ventas reales por empresa DESDE CSV
  const ventasPorEmpresaBruto = useMemo<MontoPorClave>(() => {
    const m: MontoPorClave = {};
    for (const t of TENANTS_GLOBALES) m[nombreEmpresa(t)] = 0;
    for (const r of filas) m[nombreEmpresa(r.tenant)] += getMontoReal(r);
    return m;
  }, [filas]);

  // ¿hay montos reales?
  const usarDemo = kpiBruto.totalMonto === 0;

  // VENTAS: si no hay montos usamos el demo
  const ventasPorEmpresaAll: MontoPorClave = usarDemo ? DEMO_VENTAS : ventasPorEmpresaBruto;

  // PEDIDOS: partir del DEMO y pisar solo empresas con datos reales > 0
  const pedidosPorEmpresaAll: Conteo = useMemo(() => {
    const merged: Conteo = { ...DEMO_PEDIDOS };
    for (const [empresa, cntReal] of Object.entries(pedidosPorEmpresaBruto)) {
      if (cntReal > 0) merged[empresa] = cntReal;
    }
    return merged;
  }, [pedidosPorEmpresaBruto]);

  // KPI final
  const kpi = useMemo<KPI>(() => {
    if (!usarDemo) return kpiBruto;
    const totalMonto = Object.values(ventasPorEmpresaAll).reduce((a, b) => a + b, 0);
    const totalPedidos = Object.values(pedidosPorEmpresaAll).reduce((a, b) => a + b, 0);
    const promedioPedido = totalPedidos ? totalMonto / totalPedidos : 0;
    return { totalPedidos, totalMonto, promedioPedido };
  }, [usarDemo, kpiBruto, ventasPorEmpresaAll, pedidosPorEmpresaAll]);

  /* ========== datos filtrados para gráficos superiores ========== */

  const activosGraph = selGraph.length === TENANTS_GLOBALES.length ? TENANTS_GLOBALES : selGraph;

  const filasGraph = useMemo(
    () => filas.filter((f) => activosGraph.includes(f.tenant)),
    [filas, activosGraph]
  );

  const ventasFiltradasPorEmpresa = useMemo<MontoPorClave>(() => {
    const out: MontoPorClave = {};
    for (const t of activosGraph) {
      const name = nombreEmpresa(t);
      out[name] = ventasPorEmpresaAll[name] || 0;
    }
    return out;
  }, [activosGraph, ventasPorEmpresaAll]);

  const seriePorFecha = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of filasGraph) {
      const f = (r.fecha || '').slice(0, 10);
      if (!f) continue;
      m[f] = (m[f] || 0) + getMontoReal(r);
    }

    let labels = Object.keys(m).sort();
    let data = labels.map((d) => m[d]);

    if (labels.length === 0) {
      const puntos = 7;
      const hoy = now;
      const tmpLabels: string[] = [];
      for (let i = puntos - 1; i >= 0; i--) {
        const dt = new Date(hoy);
        dt.setHours(0, 0, 0, 0);
        dt.setDate(hoy.getDate() - i);
        tmpLabels.push(dt.toISOString().slice(0, 10));
      }

      const seriesSumadas = new Array<number>(puntos).fill(0);
      for (const t of activosGraph) {
        const nombre = nombreEmpresa(t);
        const totalEmpresa = ventasPorEmpresaAll[nombre] || 0;
        const serieEmp = generaSerieEmpresa(totalEmpresa, tmpLabels, nombre);
        for (let i = 0; i < puntos; i++) seriesSumadas[i] += serieEmp[i];
      }

      labels = tmpLabels;
      data = seriesSumadas;
    }

    return { labels, data };
  }, [filasGraph, ventasPorEmpresaAll, activosGraph, now]);

  const pieLabels = useMemo(() => Object.keys(ventasFiltradasPorEmpresa), [ventasFiltradasPorEmpresa]);
  const pieData = useMemo(() => pieLabels.map((k) => ventasFiltradasPorEmpresa[k]), [pieLabels, ventasFiltradasPorEmpresa]);

  /* ================== Chart.js (superiores) ================== */

  const lineCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const pieCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const lineChartRef = useRef<Chart | null>(null);
  const pieChartRef = useRef<Chart | null>(null);

  useEffect(() => {
    // Línea
    lineChartRef.current?.destroy();
    if (lineCanvasRef.current) {
      lineChartRef.current = new Chart(lineCanvasRef.current, {
        type: 'line',
        data: {
          labels: seriePorFecha.labels,
          datasets: [
            {
              label: 'Ventas diarias (S/)',
              data: seriePorFecha.data,
              borderWidth: 3,
              borderColor: '#2563eb',
              backgroundColor: 'rgba(37,99,235,0.15)',
              tension: 0.35,
              pointRadius: 3,
              pointHoverRadius: 5,
              fill: true,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          plugins: {
            legend: { display: true, position: 'top' },
            tooltip: { callbacks: { label: (ctx) => PEN(Number(ctx.raw)) } },
          },
          scales: {
            y: { ticks: { callback: (v) => PEN(Number(v as number)) }, grid: { color: 'rgba(0,0,0,0.06)' } },
            x: { grid: { display: false } },
          },
        },
      });
    }

    // Pie
    pieChartRef.current?.destroy();
    if (pieCanvasRef.current) {
      const bgColors = EMPRESAS_UI.filter((e) => pieLabels.includes(e.label)).map((e) => e.color);
      pieChartRef.current = new Chart(pieCanvasRef.current, {
        type: 'pie',
        data: { labels: pieLabels, datasets: [{ data: pieData, borderWidth: 1, backgroundColor: bgColors }] },
        options: {
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: { boxWidth: 16, boxHeight: 16, usePointStyle: true, pointStyle: 'circle' },
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const val = Number(ctx.raw);
                  const total = pieData.reduce((a, b) => a + b, 0);
                  const pct = total ? ((val * 100) / total).toFixed(1) : '0.0';
                  return `${ctx.label}: ${PEN(val)} (${pct}%)`;
                },
              },
            },
          },
        },
      });
    }

    return () => {
      lineChartRef.current?.destroy();
      pieChartRef.current?.destroy();
    };
  }, [seriePorFecha, pieLabels, pieData]);

  /* ================== Render =================== */

  const totalPedidosAll = Object.values(pedidosPorEmpresaAll).reduce((a, b) => a + b, 0);

  return (
    <div>
      {/* Migas */}
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item">
          <Link to="/devioz">Inicio</Link>
        </li>
        <li className="breadcrumb-item active">Panel</li>
      </ol>

      <h1 className="page-header mb-3">Panel</h1>

      <div className="d-sm-flex align-items-center mb-3">
        <a href="#!" className="btn btn-dark me-2 text-truncate" id="daterange-filter">
          <i className="fa fa-calendar fa-fw text-white text-opacity-50 ms-n1" />
          <span className="ms-2">Últimos 7 días</span>
          <b className="caret ms-1 opacity-5" />
        </a>
        <div className="text-dark-300 fw-bold mt-2 mt-sm-0">Comparado con la semana anterior</div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* ===== FILA 1 ===== */}
      <div className="row">
        {/* VENTAS TOTALES */}
        <div className="col-xl-6">
          <div className="card border-0 mb-3 overflow-hidden text-dark">
            <div className="card-body">
              <div className="row">
                <div className="col-xl-7 col-lg-8">
                  <div className="mb-3 d-flex align-items-center justify-content-between">
                    <b>VENTAS TOTALES</b>
                  </div>

                  <div className="d-flex mb-1">
                    <h2 className="mb-0">{PEN(kpi.totalMonto)}</h2>
                  </div>

                  <div className="mb-3">
                    <i className="fa fa-caret-up" /> 0.00% respecto a la semana pasada
                  </div>
                  <hr />

                  <div className="row text-truncate">
                    <div className="col-6 mb-3 mb-sm-0">
                      <div>Total de pedidos</div>
                      <div className="fs-18px mb-5px fw-bold">{kpi.totalPedidos}</div>
                      <div className="progress h-5px rounded-3 bg-silver mb-5px">
                        <div
                          className="progress-bar progress-bar-striped rounded-right bg-green"
                          style={{ width: `${Math.min(100, kpi.totalPedidos)}%` }}
                        />
                      </div>
                    </div>
                    <div className="col-6">
                      <div>Promedio por pedido</div>
                      <div className="fs-18px mb-5px fw-bold">{PEN(kpi.promedioPedido)}</div>
                      <div className="progress h-5px rounded-3 bg-silver mb-5px">
                        <div className="progress-bar progress-bar-striped rounded-right" style={{ width: `${kpi.promedioPedido > 0 ? 100 : 0}%` }} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-xl-5 col-lg-4 align-items-center d-none d-lg-flex justify-content-center">
                  <img
                    src={analyticsPNG}
                    height={150}
                    className="opacity-75"
                    style={{ width: 'auto', objectFit: 'contain' }}
                    alt="Ilustración"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* VENTAS POR EMPRESA (S/) */}
        <div className="col-xl-3 col-lg-6">
          <div className="card border-0 text-dark mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <b>VENTAS POR EMPRESA (S/)</b>
                <EmpresasDropdown value={selGraph} onChange={setSelGraph} anchorLabel="Empresas:" />
              </div>
              {Object.entries(ventasFiltradasPorEmpresa)
                .sort((a, b) => (b[1] as number) - (a[1] as number))
                .map(([empresa, monto]) => (
                  <div key={empresa} className="d-flex align-items-center mb-2">
                    <div className="d-flex align-items-center">{empresa}</div>
                    <div className="d-flex align-items-center ms-auto">
                      <div className="w-100 text-end ps-2 fw-bold">{PEN(monto)}</div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* ÓRDENES POR EMPRESA */}
        <div className="col-xl-3 col-lg-6">
          <div className="card border-0 text-dark mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <b>ÓRDENES POR EMPRESA</b>
                <EmpresasDropdown value={selGraph} onChange={setSelGraph} anchorLabel="Empresas:" />
              </div>
              {Object.entries(pedidosPorEmpresaAll)
                .sort((a, b) => (b[1] as number) - (a[1] as number))
                .map(([empresa, cnt]) => {
                  const pct = totalPedidosAll > 0 ? Math.round((cnt * 10000) / totalPedidosAll) / 100 : 0;
                  return (
                    <div key={empresa} className="d-flex mb-2">
                      <div className="d-flex align-items-center">{empresa}</div>
                      <div className="d-flex align-items-center ms-auto">
                        <div className="small me-2">{pct}%</div>
                        <div className="w-50px text-end ps-2 fw-bold">{cnt}</div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>

      {/* ===== FILA 2: GRÁFICOS ===== */}
      <div className="row">
        {/* Línea */}
        <div className="col-xl-8 col-lg-6">
          <div className="card border-0 mb-3 text-dark">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                <b>VENTAS DIARIAS (SELECCIÓN)</b>
                <EmpresasDropdown value={selGraph} onChange={setSelGraph} anchorLabel="Empresas:" />
              </div>
              <div style={{ height: 380 }}>
                <canvas ref={lineCanvasRef} style={{ width: '100%', height: '100%' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Pie */}
        <div className="col-xl-4 col-lg-6">
          <div className="card border-0 mb-3 text-dark">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                <b>PARTICIPACIÓN POR EMPRESA</b>
                <EmpresasDropdown value={selGraph} onChange={setSelGraph} anchorLabel="Empresas:" />
              </div>
              <div style={{ height: 380 }}>
                <canvas ref={pieCanvasRef} style={{ width: '100%', height: '100%' }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== FILA 3 ===== */}
      <div className="row">
        {/* VENTAS POR CANAL */}
        <div className="col-xl-4 col-lg-6">
          <div className="card border-0 mb-3 text-dark">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <b>VENTAS POR CANAL</b>
                <EmpresasDropdown value={selCanal} onChange={setSelCanal} anchorLabel="Empresas:" />
              </div>
              {(() => {
                const activos = selCanal.length === TENANTS_GLOBALES.length ? TENANTS_GLOBALES : selCanal;
                const totalSel = activos.reduce((acc, s) => acc + (ventasPorEmpresaAll[nombreEmpresa(s)] || 0), 0);
                return (
                  <>
                    <h3 className="mb-10px">{PEN(totalSel)}</h3>
                    <div className="text-muted mb-2">Detalle por empresa</div>
                    <div className="d-flex flex-column gap-2">
                      {activos.map((slug) => {
                        const label = nombreEmpresa(slug);
                        const monto = ventasPorEmpresaAll[label] || 0;
                        const color = EMPRESAS_UI.find((e) => e.slug === slug)?.color || '#999';
                        return (
                          <div key={slug} className="d-flex justify-content-between align-items-center">
                            <span className="badge" style={{ background: color }}>
                              {label}
                            </span>
                            <span className="fw-bold">{PEN(monto)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* TOP PRODUCTOS POR UNIDADES */}
        <div className="col-xl-4 col-lg-6">
          <div className="card border-0 mb-3 text-dark">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <b>TOP PRODUCTOS POR UNIDADES</b>
                <EmpresasDropdown value={selTopSku} onChange={setSelTopSku} anchorLabel="Empresas:" />
              </div>

              {(() => {
                const activos = selTopSku.length === TENANTS_GLOBALES.length ? TENANTS_GLOBALES : selTopSku;

                // contador por SKU
                const contador: Record<string, number> = {};

                activos.forEach((slug) => {
                  const label = nombreEmpresa(slug);
                  const base = pedidosPorEmpresaAll[label] || 0; // aquí ya está Gloria = 58
                  const skus = DEMO_SKUS[slug] ?? ['Producto'];
                  const pesos = skus.map((s, i) => 0.8 + 0.4 * Math.sin((i + 1) * 1.3) * (0.9 + hash01(label + s) * 0.2));
                  const suma = pesos.reduce((a, b) => a + b, 0) || 1;
                  skus.forEach((sku, i) => {
                    const unidades = Math.round((pesos[i] / suma) * base);
                    contador[`${label} · ${sku}`] = (contador[`${label} · ${sku}`] || 0) + unidades;
                  });
                });

                let top: Array<[string, number]> = Object.entries(contador).sort((a, b) => b[1] - a[1]);

                // si se están viendo TODAS y Gloria no entró, la empujamos
                if (selTopSku.length === TENANTS_GLOBALES.length) {
                  const claveGloria = 'Gloria · Leche Evaporada';
                  const yaEsta = top.find(([k]) => k === claveGloria);
                  if (!yaEsta) top.push([claveGloria, 27]);
                }

                top = top.slice(0, 5);

                if (!top.length) return <div className="text-muted">Sin datos</div>;

                return (
                  <div className="d-flex flex-column gap-3">
                    {top.map(([nombre, unidades], i) => (
                      <div key={nombre} className="d-flex align-items-center justify-content-between">
                        <div className="text-truncate me-2">
                          <div className="fw-semibold">{nombre}</div>
                          <div className="text-primary small">{unidades} unidades</div>
                        </div>
                        <div className="ms-2 text-end">
                          <div className="small text-muted">Ranking #{i + 1}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* PROMOCIONES ACTIVAS POR EMPRESA */}
        <div className="col-xl-4 col-lg-6">
          <div className="card border-0 mb-3 text-dark">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <b>PROMOCIONES ACTIVAS POR EMPRESA</b>
                <EmpresasDropdown value={selPromo} onChange={setSelPromo} anchorLabel="Empresas:" />
              </div>

              {(() => {
                const activos = selPromo.length === TENANTS_GLOBALES.length ? TENANTS_GLOBALES : selPromo;
                if (!activos.length) return <div className="text-muted">Sin datos</div>;

                return (
                  <div className="d-flex flex-column gap-3">
                    {activos.map((slug) => {
                      const label = nombreEmpresa(slug);
                      const avance = pctFor(label + '-promo');
                      const alcance = pctFor(label + '-alcance');
                      return (
                        <div key={slug} className="border rounded p-2">
                          <div className="d-flex justify-content-between">
                            <div className="fw-semibold">{label} · Promo semanal</div>
                            <span className="small text-muted">Últimos 7 días</span>
                          </div>
                          <div className="progress my-2" style={{ height: 6 }}>
                            <div className="progress-bar bg-primary" style={{ width: `${avance}%` }} />
                          </div>
                          <div className="small text-muted mb-1">Progreso objetivo: {avance}%</div>

                          <div className="progress my-2" style={{ height: 6 }}>
                            <div className="progress-bar bg-warning" style={{ width: `${alcance}%` }} />
                          </div>
                          <div className="small text-muted">Alcance estimado: {alcance}%</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {cargando && <div className="text-muted small mt-2">Cargando datos de BCP, Bembos, Claro, PlazaVea y Gloria…</div>}
    </div>
  );
}
